import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    ListGroup,
    ListGroupItem,
} from 'reactstrap'
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const dividerBorder = '1px solid rgba(255, 255, 255, 0.15)'
const listItemBorderColor = 'rgba(255, 255, 255, 0.05)'

interface Device {
    isMonitored?: boolean
    lastSeenStatus?: string
    oilAlarm?: boolean
}

interface DevicesState {
    devices: {
        data: Device[]
    }
}

const ActiveDevicesChart = () => {
    const { t } = useTranslation()
    const devices = useSelector((state: DevicesState) => state.devices.data) ?? []

    const devicesCount = devices.length
    const failureCount = devices.filter(device => device.lastSeenStatus !== 'ok' && device.isMonitored).length
    const oilAlarmCount = devices.filter(device => device.oilAlarm === true && device.isMonitored).length
    const isMonitoredCount = devices.filter(device => device.isMonitored === true).length
    const isNotMonitoredCount = devices.filter(device => device.isMonitored === false).length

    return (
        <Card className="h-100 bg-gradient">
            <CardHeader className="bg-transparent">
                <Link to="/devices/all">
                    <h5 className="text-white">{t('device.devices')}</h5>
                    <div className="real-time-user display-1 font-weight-normal text-white">
                        {devicesCount}
                    </div>
                </Link>
            </CardHeader>
            <CardBody className="text-white fs--1">
                <ListGroup flush className="mt-4">
                    <ListGroupItem
                        className="bg-transparent d-flex justify-content-between px-0 py-1 font-weight-semi-bold border-top-0"
                        style={{ borderColor: listItemBorderColor }}
                    >
                        <p className="mb-0" />
                        <p className="mb-0 ml-auto">{t('device.quantity')}</p>
                    </ListGroupItem>
                    <Link to="/devices" className="text-light">
                        <ListGroupItem
                            className="bg-transparent d-flex justify-content-between px-0 py-1"
                            style={{ borderColor: listItemBorderColor }}
                        >
                            <p className="mb-0">{t('device.monitored')}</p>
                            <p className="mb-0">{isMonitoredCount}</p>
                        </ListGroupItem>
                    </Link>
                    <Link to="/devices/not-monitored" className="text-light">
                        <ListGroupItem
                            className="bg-transparent d-flex justify-content-between px-0 py-1"
                            style={{ borderColor: listItemBorderColor }}
                        >
                            <p className="mb-0">{t('device.notMonitored')}</p>
                            <p className="mb-0">{isNotMonitoredCount}</p>
                        </ListGroupItem>
                    </Link>
                    <ListGroupItem
                        className="bg-transparent d-flex justify-content-between px-0 py-1 d-none"
                        style={{ borderColor: listItemBorderColor }}
                    >
                        <p className="mb-0">{t('failure')}</p>
                        <p className="mb-0">{failureCount}</p>
                    </ListGroupItem>
                    <ListGroupItem
                        className="bg-transparent d-flex justify-content-between px-0 py-1 d-none"
                        style={{ borderColor: listItemBorderColor }}
                    >
                        <p className="mb-0">{t('oilShortage')}</p>
                        <p className="mb-0">{oilAlarmCount}</p>
                    </ListGroupItem>
                </ListGroup>
            </CardBody>
            <CardFooter
                className="text-right bg-transparent"
                style={{ borderTop: dividerBorder }}
            >
                <Link className="text-white" to="/devices">
                    {t('device.list')}
                    <FontAwesomeIcon
                        icon="chevron-right"
                        transform="down-1"
                        className="ml-1 fs--1"
                    />
                </Link>
            </CardFooter>
        </Card>
    )
}

export default ActiveDevicesChart
